import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModuleLink as Link } from '../../components/ModuleLink';
import Layout from '../../components/Layout';
import StepIndicator from '../../components/StepIndicator';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { useApiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import OneDrivePicker, { type OneDriveFile } from '../../components/OneDrivePicker';
import { safeJson, fmtDate, fmtDateTime, statusBadgeClass, responseBadgeClass, surveyBadgeClass, AMENDABLE_STATUSES } from '../../utils';
import { AmendmentList } from '../AmendChange';
import type { AmendmentsResponse, ChangeComment, ChangeRequest, StakeholderResponse, ChangeLog, ImplementationPlan, LessonsLearned, DeviationLogData, EvidenceLogData, SurveyLogData, Approver } from '../../types';

type TabId = 'summary' | 'engagement' | 'plan' | 'deviations' | 'surveys' | 'discussion' | 'audit';

function ScorePill({ value }: { value: number | null | undefined }) {
  if (value == null) return <span>—</span>;
  return <span className={`score-pill-display${value < 5 ? ' is-low' : ''}`}>{value}</span>;
}

export default function ChangeDetail() {
  const [searchParams]  = useSearchParams();
  const cid             = searchParams.get('cid') ?? '';
  const { userEmail } = useAuth();
  const api           = useApiClient();

  const [loading, setLoading]     = useState(true);
  const [notFound, setNotFound]   = useState(false);
  const [record, setRecord]       = useState<ChangeRequest | null>(null);
  const [responses, setResponses] = useState<StakeholderResponse[]>([]);
  const [logs, setLogs]           = useState<ChangeLog[]>([]);
  const [amendments, setAmendments] = useState<AmendmentsResponse | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const { showToast } = useToast();
  // null = the server said this person is not part of the change (no discussion, no evidence form)
  const [comments, setComments]   = useState<ChangeComment[] | null>(null);
  const [commentText, setCommentText] = useState('');
  const [posting, setPosting]     = useState(false);
  const [evFile, setEvFile]       = useState<OneDriveFile | null>(null);
  const [evName, setEvName]       = useState('');
  const [evUrl, setEvUrl]         = useState('');
  const [evDesc, setEvDesc]       = useState('');
  const [attaching, setAttaching] = useState(false);

  async function postComment() {
    if (!commentText.trim()) { showToast('Write a comment first.', 'warn'); return; }
    setPosting(true);
    try {
      const row = await api.post<ChangeComment>(`/changes/${encodeURIComponent(cid)}/comments`, { body: commentText });
      setComments(prev => [...(prev ?? []), row]);
      setCommentText('');
    } catch (err) { showToast((err as Error).message, 'error'); }
    finally { setPosting(false); }
  }

  async function attachEvidence() {
    const fileName = (evFile?.name ?? evName).trim();
    const fileUrl  = (evFile?.url ?? evUrl).trim();
    if (!fileName || !fileUrl) { showToast('Choose a file from OneDrive, or give a name and a link.', 'warn'); return; }
    setAttaching(true);
    try {
      await api.post(`/changes/${encodeURIComponent(cid)}/evidence`, { fileName, fileUrl, fileDescription: evDesc });
      setLogs(await api.get<ChangeLog[]>(`/changes/${encodeURIComponent(cid)}/logs`));
      setEvFile(null); setEvName(''); setEvUrl(''); setEvDesc('');
      showToast('Evidence attached.', 'success');
    } catch (err) { showToast((err as Error).message, 'error'); }
    finally { setAttaching(false); }
  }
  useEffect(() => {
    if (!cid) { setNotFound(true); setLoading(false); return; }
    async function load() {
      try {
        const [rec, resp, logData] = await Promise.all([
          api.get<ChangeRequest>(`/changes/${encodeURIComponent(cid)}`),
          api.get<StakeholderResponse[]>(`/changes/${encodeURIComponent(cid)}/responses`),
          api.get<ChangeLog[]>(`/changes/${encodeURIComponent(cid)}/logs`),
        ]);
        if (!rec) { setNotFound(true); return; }
        setRecord(rec);
        setResponses(resp);
        setLogs(logData);
        // Amendment history is only shown to people on the change; for anyone else the server says 403 and we simply show none.
        api.get<AmendmentsResponse>(`/changes/${encodeURIComponent(cid)}/amendments`).then(setAmendments).catch(() => setAmendments(null));
        api.get<ChangeComment[]>(`/changes/${encodeURIComponent(cid)}/comments`).then(setComments).catch(() => setComments(null));
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    }
    load();
  }, [cid]);

  if (loading) return <Layout><div className="loading-state"><div className="spinner spinner--lg" /><span>Loading…</span></div></Layout>;
  if (notFound || !record) return (
    <Layout>
      <div className="card">
        <div className="state-card">
          <div className="state-card__icon"><i className="fa-solid fa-circle-question" aria-hidden="true" /></div>
          <h2 className="state-card__title">Change Not Found</h2>
          <p className="state-card__body">The change record could not be found.</p>
          <Link to="/" className="btn btn--secondary" style={{ marginTop: 8 }}>Back to Home</Link>
        </div>
      </div>
    </Layout>
  );

  const plan = safeJson<Partial<ImplementationPlan>>(record.implementationPlan, {});
  // A plan (or a draft of one) is shown as soon as ANY of its fields has content. Showing the body only when an approach had been
  // chosen made a draft that had text but no approach look empty to everyone else.
  const hasPlanContent = ([
    plan.approach, plan.milestones, plan.trainingRequirements, plan.communicationPlan, plan.risks, plan.resourceCosts,
    plan.goLiveDate, plan.stabilisationPeriod, plan.uploadedFileUrl,
  ] as Array<string | undefined>).some(v => typeof v === 'string' && v.trim() !== '');
  const ll   = safeJson<Partial<LessonsLearned>>(record.lessonsLearned, {});
  const deviations  = logs.filter(l => l.logType === 'Deviation');
  const evidenceLogs = logs.filter(l => l.logType === 'Evidence');
  const surveyLogs = logs.filter(l => l.logType === 'Survey Response' && safeJson<Partial<SurveyLogData>>(l.logData, {}).status === 'Responded');

  // Audit events
  type AuditEvent = { ts: Date; label: string; actor: string; detail?: string };
  const auditEvents: AuditEvent[] = [];
  const addAudit = (ts: string | null | undefined, label: string, actor: string) => { if (ts) auditEvents.push({ ts: new Date(ts), label, actor }); };
  addAudit(record.dateSubmitted,        'Change Request Submitted',          record.leadName);
  addAudit(record.approvalSubmittedDate,'Submitted for Approval',            record.leadName);
  addAudit(record.approvalDate,         `Decision: ${record.approvalDecision ?? 'Pending'}`, safeJson<Approver[]>(record.approvers, []).map(a => a.name).join(', '));
  addAudit(record.goliveCommunicationDate, 'Go-Live Communication Sent',    'System');
  addAudit(record.closureSubmittedDate, 'Submitted for Closure',             record.leadName);
  addAudit(record.sponsorSignoffDate,   'Sponsor Confirmed Closure',         record.sponsorName);
  addAudit(record.closureDate,          'Change Closed',                     'System');
  responses.forEach(r => {
    addAudit(r.notificationSentOn, `Notified — ${r.teamLeadName}`,   'System');
    addAudit(r.dateResponded,      `Responded — ${r.teamLeadName}`,  r.teamLeadName);
    if (r.signoffDate && r.signoffStatus === 'Signed Off') addAudit(r.signoffDate, `Signed Off — ${r.teamLeadName}`, r.teamLeadName);
  });
  logs.forEach(l => {
    if (l.loggedOn) addAudit(l.loggedOn, l.logType === 'Deviation' ? `Deviation Logged` : `Survey Responded — ${l.loggedByName}`, l.loggedByName);
    addAudit(l.escalationDate, `Deviation Escalated to Sponsor`, 'System');
  });
  (amendments?.amendments ?? []).forEach(a => auditEvents.push({
    ts: new Date(a.amendedOn), label: `Amended — Section ${a.sectionNumber}: ${a.sectionTitle}${a.afterSignoff === 'Yes' ? ' (after sign-off)' : ''}`,
    actor: a.amendedByName, detail: a.reason,
  }));
  auditEvents.sort((a, b) => a.ts.getTime() - b.ts.getTime());

  const TABS: { id: TabId; label: string; icon: string }[] = [
    { id: 'summary',    label: 'Summary',                 icon: 'fa-file-lines' },
    { id: 'engagement', label: 'Stakeholder Engagement',  icon: 'fa-users' },
    { id: 'plan',       label: 'Implementation Plan',     icon: 'fa-clipboard-list' },
    { id: 'deviations', label: 'Deviations',              icon: 'fa-triangle-exclamation' },
    { id: 'surveys',    label: 'Survey Results',          icon: 'fa-chart-bar' },
    ...(comments !== null ? [{ id: 'discussion' as TabId, label: `Discussion${comments.length ? ` (${comments.length})` : ''}`, icon: 'fa-comments' }] : []),
    { id: 'audit',      label: 'Audit Trail',             icon: 'fa-clock-rotate-left' },
  ];

  return (
    <Layout>
      <div className="page-header">
        <div className="page-header__title">
          <div className="breadcrumb">
            <Link to="/" className="breadcrumb__link"><i className="fa-solid fa-house" aria-hidden="true" /> Home</Link>
            <span className="breadcrumb__sep">/</span>
            <span className="breadcrumb__current">{record.changeId}</span>
          </div>
          <h1>{record.changeName}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
            <span className={`badge ${statusBadgeClass(record.status)}`}>{record.status}</span>
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>{record.changeId} · {record.leadName} · {record.scope}</span>
          </div>
        </div>
        {record.leadEmail.toLowerCase() === userEmail.toLowerCase() && (
          <div className="page-header__actions">
            {AMENDABLE_STATUSES.includes(record.status) && (
              <Link to={`/amend-change?cid=${encodeURIComponent(cid)}`} className="btn btn--secondary">
                <i className="fa-solid fa-pen-to-square" aria-hidden="true" /> Edit submission
              </Link>
            )}
            {!['Closed','Rejected'].includes(record.status) && (
              <Link to={`/manage-change?cid=${encodeURIComponent(cid)}`} className="btn btn--primary">
                <i className="fa-solid fa-arrow-right" aria-hidden="true" /> Manage Change
              </Link>
            )}
          </div>
        )}
      </div>

      <StepIndicator currentStep={record.currentStep} />

      {/* Tabs */}
      <div className="tabs" role="tablist" aria-label="Change detail sections">
        {TABS.map(t => (
          <button key={t.id} className={`tab${activeTab === t.id ? ' is-active' : ''}`} role="tab" aria-selected={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
            <i className={`fa-solid ${t.icon}`} aria-hidden="true" /> {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Summary ── */}
      {activeTab === 'summary' && (
        <>
          <div className="section-panel" style={{ marginBottom: 16 }}>
            <div className="section-panel__header"><h2 className="section-panel__title">Change Details</h2></div>
            <div className="section-panel__body">
              <div className="field-display-grid">
                {[['Change ID', <span key="cid" className="change-id-mono">{record.changeId}</span>], ['Change Name', record.changeName], ['Change Lead', record.leadName], ['Sponsor', record.sponsorName], ['Approver', safeJson<Approver[]>(record.approvers, []).map(a => a.name).join(', ')], ['Scope', record.scope], ['Date Submitted', fmtDate(record.dateSubmitted)], ['Proposed Go-Live', fmtDate(record.proposedGolive)], ['Confirmed Go-Live', fmtDate(record.confirmedGolive)], ['Status', <StatusBadge key="s" status={record.status} />], ['Current Step', record.currentStep], ['Repository', record.repositoryUrl ? <a key="repo" href={record.repositoryUrl} target="_blank" rel="noopener noreferrer" className="repo-link"><i className="fa-solid fa-folder-open" aria-hidden="true" /> View Repository</a> : null]].map(([l, v], i) => (
                  <div key={i} className="field-display"><div className="field-display__label">{l as string}</div><div className="field-display__value">{v ?? <span className="field-display__value--empty">—</span>}</div></div>
                ))}
              </div>
            </div>
          </div>

          <div className="section-panel" style={{ marginBottom: 16 }}>
            <div className="section-panel__header"><h2 className="section-panel__title">Description &amp; Risk</h2></div>
            <div className="section-panel__body">
              <div className="field-display-grid">
                {[['Current State', record.currentState], ['New State', record.newState], ['Benefits / Reason', record.benefitsReason], ['Risk if Implemented', record.riskIfImplemented], ['Risk if NOT Implemented', record.riskIfNotImplemented]].map(([l, v]) => (
                  <div key={l as string} className="field-display"><div className="field-display__label">{l as string}</div><div className="field-display__value"><pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem' }}>{(v as string) ?? '—'}</pre></div></div>
                ))}
              </div>
            </div>
          </div>

          {ll.wentWell && (
            <div className="section-panel" style={{ marginBottom: 16 }}>
              <div className="section-panel__header"><h2 className="section-panel__title">Lessons Learned</h2></div>
              <div className="section-panel__body">
                <div className="field-display-grid">
                  {[['What went well?', ll.wentWell], ['What could be done better?', ll.improvements], ['Recommendations', ll.recommendations]].filter(([,v]) => v).map(([l, v]) => (
                    <div key={l as string} className="field-display"><div className="field-display__label">{l as string}</div><div className="field-display__value"><pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem' }}>{v as string}</pre></div></div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </>
      )}

      {/* ── Tab: Stakeholder Engagement ── */}
      {activeTab === 'engagement' && (
        <div className="table-wrapper">
          <table className="table" aria-label="Stakeholder engagement">
            <thead>
              <tr><th>Name</th><th>Dept</th><th>Notified</th><th>Response</th><th>Affected</th><th>Concerns</th><th>Support Needs</th><th>Effort</th><th>Sign-Off</th><th>Sign-Off Date</th><th>Resolution</th></tr>
            </thead>
            <tbody>
              {responses.length === 0 && <tr><td colSpan={11} className="table-empty">No stakeholder responses found.</td></tr>}
              {responses.map(r => (
                <tr key={r.id}>
                  <td data-label="Name">{r.teamLeadName}</td>
                  <td data-label="Dept">{r.department}</td>
                  <td data-label="Notified">{fmtDate(r.notificationSentOn)}</td>
                  <td data-label="Response"><span className={`badge ${responseBadgeClass(r.responseStatus)}`}>{r.responseStatus}</span></td>
                  <td data-label="Affected">{r.isAffected ? <span className={`badge ${r.isAffected === 'Yes' ? 'badge--warn' : 'badge--muted'}`}>{r.isAffected}</span> : '—'}</td>
                  <td data-label="Concerns" style={{ fontSize: '0.8125rem' }}>{r.concerns ?? '—'}</td>
                  <td data-label="Support Needs" style={{ fontSize: '0.8125rem' }}>{r.supportNeeds ?? '—'}</td>
                  <td data-label="Effort">{r.effortEstimate ?? '—'}</td>
                  <td data-label="Sign-Off"><span className={`badge ${responseBadgeClass(r.signoffStatus)}`}>{r.signoffStatus}</span></td>
                  <td data-label="Sign-Off Date">{fmtDate(r.signoffDate)}</td>
                  <td data-label="Resolution" style={{ fontSize: '0.8125rem' }}>{r.concernResolution ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Tab: Implementation Plan ── */}
      {activeTab === 'plan' && (
        <>
        <div className="section-panel">
          <div className="section-panel__header"><h2 className="section-panel__title">Implementation Plan</h2></div>
          <div className="section-panel__body">
            {plan.draft && (
              <div className="alert alert--warn" style={{ marginBottom: 16 }}>
                <div className="alert__body"><strong>Draft: not yet submitted for sign-off.</strong> This is the Change Lead's working draft{plan.draftSavedBy ? ` (saved by ${plan.draftSavedBy}` : ''}{plan.draftSavedOn ? `${plan.draftSavedBy ? ' on ' : ' (saved '}${fmtDateTime(plan.draftSavedOn)}` : ''}{plan.draftSavedBy || plan.draftSavedOn ? ')' : ''}. Stakeholders have not been asked to sign it off and approvers should not rely on it yet.</div>
              </div>
            )}
            {!hasPlanContent ? <p className="text-muted">Implementation plan not yet submitted.</p> : (
              <>
                <div className="field-display-grid" style={{ marginBottom: 16 }}>
                  {[['Approach', plan.approach], ['Go-Live Date', fmtDate(plan.goLiveDate)], ['Stabilisation Period', plan.stabilisationPeriod], ['Plan Version', plan.draft ? `${plan.planVersion ?? 'v1.0'} (draft)` : plan.planVersion]].map(([l, v]) => (
                    <div key={l as string} className="field-display"><div className="field-display__label">{l as string}</div><div className="field-display__value">{(v as string) || '—'}</div></div>
                  ))}
                </div>
                <div className="divider" />
                <div className="field-display-grid">
                  {[['Key Milestones', plan.milestones], ['Training Requirements', plan.trainingRequirements], ['Communication Plan', plan.communicationPlan], ['Risks & Mitigations', plan.risks], ['Resource & Cost', plan.resourceCosts]].map(([l, v]) => (
                    <div key={l as string} className="field-display"><div className="field-display__label">{l as string}</div><div className="field-display__value"><pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem' }}>{(v as string) || '—'}</pre></div></div>
                  ))}
                </div>
                {plan.uploadedFileUrl && <div style={{ marginTop: 16 }}><a href={plan.uploadedFileUrl} target="_blank" rel="noopener noreferrer" className="btn btn--secondary btn--sm"><i className="fa-solid fa-file-lines" aria-hidden="true" /> {plan.uploadedFileName || 'View Plan Document'}</a></div>}
              </>
            )}
          </div>
        </div>

        {/* Evidence */}
        <div className="section-panel" style={{ marginTop: 16 }}>
          <div className="section-panel__header"><h2 className="section-panel__title"><i className="fa-solid fa-paperclip" aria-hidden="true" /> Evidence</h2></div>
          <div className="section-panel__body">
            {evidenceLogs.length === 0
              ? <p className="text-muted">No evidence attached.</p>
              : evidenceLogs.map(log => {
                  const d = safeJson<EvidenceLogData>(log.logData, { fileName: '', fileDescription: '', fileUrl: '' });
                  return (
                    <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 8, background: 'var(--surface-muted)' }}>
                      <i className="fa-solid fa-file-lines" style={{ color: 'var(--primary)', flexShrink: 0 }} aria-hidden="true" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }}>{d.fileName || 'File'}</a>
                        {d.fileDescription && <div style={{ fontSize: '0.8125rem', color: 'var(--ink-dim)' }}>{d.fileDescription}</div>}
                        <div style={{ fontSize: '0.8125rem', color: 'var(--ink-dim)' }}>{log.loggedByName} · {fmtDate(log.loggedOn)}</div>
                      </div>
                    </div>
                  );
                })
            }
            {comments !== null && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <div className="form-label">Attach evidence</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                  <OneDrivePicker onSelect={f => { setEvFile(f); setEvName(''); setEvUrl(''); }} disabled={attaching} label="Choose from OneDrive" />
                  {evFile && <span style={{ fontSize: '0.8125rem' }}><i className="fa-solid fa-file-lines" aria-hidden="true" /> {evFile.name}</span>}
                </div>
                {!evFile && (
                  <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
                    <span className="text-muted" style={{ fontSize: '0.8125rem' }}>Or paste a link to a file (an https link from OneDrive or SharePoint).</span>
                    <input className="form-control" aria-label="File name" placeholder="File name, e.g. Evidence pack.docx" value={evName} onChange={e => setEvName(e.target.value)} disabled={attaching} />
                    <input className="form-control" aria-label="File link" placeholder="https://…" value={evUrl} onChange={e => setEvUrl(e.target.value)} disabled={attaching} />
                  </div>
                )}
                <input className="form-control" aria-label="What this file shows" placeholder="What this file shows (optional)" value={evDesc} onChange={e => setEvDesc(e.target.value)} disabled={attaching} style={{ marginBottom: 10 }} />
                <button className="btn btn--primary btn--sm" onClick={attachEvidence} disabled={attaching}>
                  <i className="fa-solid fa-paperclip" aria-hidden="true" /> {attaching ? 'Attaching…' : 'Attach evidence'}
                </button>
              </div>
            )}
          </div>
        </div>
        </>
      )}

      {/* ── Tab: Deviations ── */}
      {activeTab === 'deviations' && (
        <div className="table-wrapper">
          <table className="table" aria-label="Deviation log">
            <thead><tr><th>Logged By</th><th>Logged On</th><th>Description</th><th>Impact</th><th>Escalated</th><th>Acknowledged</th></tr></thead>
            <tbody>
              {deviations.length === 0 && <tr><td colSpan={6} className="table-empty">No deviations logged.</td></tr>}
              {deviations.map(d => {
                const data = safeJson<DeviationLogData>(d.logData, { description: '', impactAssessment: '' });
                return (
                  <tr key={d.id}>
                    <td data-label="Logged By">{d.loggedByName}</td>
                    <td data-label="Logged On">{fmtDateTime(d.loggedOn)}</td>
                    <td data-label="Description" style={{ fontSize: '0.8125rem' }}>{data.description}</td>
                    <td data-label="Impact" style={{ fontSize: '0.8125rem' }}>{data.impactAssessment}</td>
                    <td data-label="Escalated">{d.escalatedToSponsor === 'Yes' ? <span className="badge badge--warn">Yes</span> : <span className="badge badge--muted">No</span>}</td>
                    <td data-label="Acknowledged">{d.sponsorAcknowledged === 'Yes' ? <span className="badge badge--accent">Yes</span> : <span className="badge badge--muted">No</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Tab: Survey Results ── */}
      {activeTab === 'surveys' && (
        <>
          <div className="section-panel" style={{ marginBottom: 16 }}>
            <div className="section-panel__header"><h2 className="section-panel__title">Survey Summary</h2></div>
            <div className="section-panel__body">
              <div className="field-display-grid">
                <div className="field-display"><div className="field-display__label">Surveys Sent</div><div className="field-display__value">{logs.filter(l => l.logType === 'Survey Response').length}</div></div>
                <div className="field-display"><div className="field-display__label">Responses</div><div className="field-display__value">{surveyLogs.length}</div></div>
                <div className="field-display"><div className="field-display__label">Average Score</div><div className="field-display__value">{record.surveyScore != null ? <strong style={{ fontSize: '1.25rem', color: 'var(--primary)' }}>{record.surveyScore}</strong> : '—'}{record.surveyScore != null ? ' / 10' : ''}</div></div>
                <div className="field-display"><div className="field-display__label">Survey Status</div><div className="field-display__value">{record.surveyStatus ? <span className={`badge ${surveyBadgeClass(record.surveyStatus)}`}>{record.surveyStatus}</span> : '—'}</div></div>
              </div>
            </div>
          </div>
          <div className="table-wrapper">
            <table className="table" aria-label="Survey responses">
              <thead><tr><th>Respondent</th><th>Awareness</th><th>Adoption</th><th>Communication</th><th>Training</th><th>Average</th><th>Unresolved Concerns</th></tr></thead>
              <tbody>
                {surveyLogs.length === 0 && <tr><td colSpan={7} className="table-empty">No survey responses received.</td></tr>}
                {surveyLogs.map(l => {
                  const d = safeJson<SurveyLogData>(l.logData, {} as SurveyLogData);
                  return (
                    <tr key={l.id}>
                      <td data-label="Respondent">{l.loggedByName}</td>
                      <td data-label="Awareness"><ScorePill value={d.awareness} /></td>
                      <td data-label="Adoption"><ScorePill value={d.adoption} /></td>
                      <td data-label="Communication"><ScorePill value={d.communicationAdequacy} /></td>
                      <td data-label="Training"><ScorePill value={d.trainingSupportAdequacy} /></td>
                      <td data-label="Average"><ScorePill value={d.averageScore} /></td>
                      <td data-label="Concerns" style={{ fontSize: '0.8125rem' }}>{d.unresolvedConcerns ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Tab: Discussion ── */}
      {activeTab === 'discussion' && comments !== null && (
        <div className="section-panel">
          <div className="section-panel__header"><h2 className="section-panel__title"><i className="fa-solid fa-comments" aria-hidden="true" /> Discussion</h2></div>
          <div className="section-panel__body">
            {comments.length === 0
              ? <p className="text-muted">No comments yet. Questions and answers about this change go here, so they stay on the record.</p>
              : <div className="timeline">
                  {comments.map(c => (
                    <div key={c.id} className="timeline-event">
                      <div className="timeline-event__label" style={{ whiteSpace: 'pre-wrap', fontWeight: 400 }}>{c.body}</div>
                      <div className="timeline-event__meta">{c.authorName} · {fmtDateTime(c.createdAt)}</div>
                    </div>
                  ))}
                </div>
            }
            <div style={{ marginTop: 16 }}>
              <label className="form-label" htmlFor="new-comment">Add a comment</label>
              <textarea id="new-comment" className="form-control" rows={4} value={commentText} onChange={e => setCommentText(e.target.value)} disabled={posting} />
              <button className="btn btn--primary btn--sm" style={{ marginTop: 10 }} onClick={postComment} disabled={posting}>
                <i className="fa-solid fa-paper-plane" aria-hidden="true" /> {posting ? 'Posting…' : 'Post comment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Audit Trail ── */}
      {activeTab === 'audit' && (
        <div className="section-panel">
          <div className="section-panel__header"><h2 className="section-panel__title">Audit Trail</h2></div>
          <div className="section-panel__body">
            {auditEvents.length === 0
              ? <p className="text-muted">No audit events found.</p>
              : <div className="timeline">
                  {auditEvents.map((e, i) => (
                    <div key={i} className="timeline-event">
                      <div className="timeline-event__label">{e.label}</div>
                      <div className="timeline-event__meta">{e.actor} · {fmtDateTime(e.ts.toISOString())}</div>
                      {e.detail && <div className="timeline-event__meta">Reason: {e.detail}</div>}
                    </div>
                  ))}
                </div>
            }
          </div>
        </div>
      )}

      {activeTab === 'audit' && amendments && amendments.amendments.length > 0 && (
        <div className="section-panel" style={{ marginTop: 16 }}>
          <div className="section-panel__header"><h2 className="section-panel__title">Amendments to the submission</h2></div>
          <div className="section-panel__body"><AmendmentList amendments={amendments.amendments} /></div>
        </div>
      )}
    </Layout>
  );
}
